import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Form, Button, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers, 
    faShoppingCart, 
    faEuroSign, 
    faChartLine, 
    faStar,
    faTrophy,
    faGift,
    faStore,
    faCrown,
    faUserTie,
    faFilter,
    faChevronDown,
    faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

// Registra i componenti di Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const EcommerceDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [origine, setOrigine] = useState('');  // Nuovo stato per filtro origine
    const [statiSelezionati, setStatiSelezionati] = useState([]);  // Stati ordini selezionati
    const [periodoPredef, setPeriodoPredef] = useState('1_mese');  // Periodo predefinito

    const handleClientClick = (clientId) => {
        navigate(`/clienti/${clientId}`);
    };

    useEffect(() => {
        // Imposta date di default (ultimi 30 giorni) e passa i valori direttamente
        // perché setStartDate/setEndDate sono async e fetchStatistics() li leggerebbe ancora null
        handlePeriodoChange('1_mese');
        const today = new Date();
        const endStr = today.toISOString().split('T')[0];
        const startObj = new Date(today);
        startObj.setMonth(today.getMonth() - 1);
        const startStr = startObj.toISOString().split('T')[0];
        fetchStatistics(startStr, endStr);
    }, []);

    const handlePeriodoChange = (periodo) => {
        const today = new Date();
        const endDateStr = today.toISOString().split('T')[0];
        let startDateObj = new Date(today);
        
        switch(periodo) {
            case '1_mese':
                startDateObj.setMonth(today.getMonth() - 1);
                break;
            case '3_mesi':
                startDateObj.setMonth(today.getMonth() - 3);
                break;
            case '6_mesi':
                startDateObj.setMonth(today.getMonth() - 6);
                break;
            case '1_anno':
                startDateObj.setFullYear(today.getFullYear() - 1);
                break;
            case 'personalizzato':
                // Non cambiare le date, l'utente le seleziona manualmente
                setPeriodoPredef('personalizzato');
                return;
            default:
                startDateObj.setMonth(today.getMonth() - 1);
        }
        
        setPeriodoPredef(periodo);
        setEndDate(endDateStr);
        setStartDate(startDateObj.toISOString().split('T')[0]);
    };

    const fetchStatistics = async (start = null, end = null, orig = null, stati = null) => {
        try {
            setLoading(true);
            setError('');
            
            const token = localStorage.getItem('access_token');
            const params = {};
            if (start) params.start_date = start;
            if (end) params.end_date = end;
            if (orig) params.origine = orig;
            if (stati && stati.length > 0) params.stati = stati.join(',');
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/statistics/ecommerce`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params
                }
            );
            
            setStats(response.data);
        } catch (err) {
            console.error('Errore nel caricamento statistiche:', err);
            setError('Errore nel caricamento delle statistiche');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchStatistics(startDate, endDate, origine, statiSelezionati);
    };

    const resetFilters = () => {
        setOrigine('');  // Reset origine
        setStatiSelezionati([]);  // Reset stati
        handlePeriodoChange('1_mese');
        
        // Recupera le date dopo il reset
        const today = new Date();
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        
        fetchStatistics(oneMonthAgo.toISOString().split('T')[0], today.toISOString().split('T')[0], '', []);
    };

    const renderKPICard = (title, value, icon, color, subtitle = null) => (
        <Col md={6} lg={3} className="mb-4">
            <Card className="h-100 shadow-sm border-0" style={{ borderLeft: `4px solid ${color}` }}>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <p className="text-muted mb-1 small">{title}</p>
                            <h3 className="mb-0 fw-bold">{value}</h3>
                            {subtitle && <small className="text-muted">{subtitle}</small>}
                        </div>
                        <div 
                            style={{ 
                                backgroundColor: `${color}20`, 
                                borderRadius: '50%', 
                                width: '50px', 
                                height: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FontAwesomeIcon icon={icon} size="lg" style={{ color }} />
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );

    if (loading && !stats) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    // Preparazione dati per grafici
    const monthlyChartData = stats?.trends?.monthly ? {
        labels: stats.trends.monthly.map(m => {
            const [year, month] = m.month.split('-');
            const date = new Date(year, month - 1);
            return date.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
        }),
        datasets: [
            {
                label: 'Ordini',
                data: stats.trends.monthly.map(m => m.orders),
                borderColor: '#007bff',
                backgroundColor: '#007bff20',
                fill: true,
                tension: 0.4
            }
        ]
    } : null;

    const revenueChartData = stats?.trends?.monthly ? {
        labels: stats.trends.monthly.map(m => {
            const [year, month] = m.month.split('-');
            const date = new Date(year, month - 1);
            return date.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
        }),
        datasets: [
            {
                label: 'Fatturato (€)',
                data: stats.trends.monthly.map(m => m.revenue),
                backgroundColor: '#28a745',
                borderColor: '#28a745',
                borderWidth: 2
            }
        ]
    } : null;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            }
        },
        scales: {
            y: {
                beginAtZero: true
            }
        }
    };

    return (
        <>
            <style>{`
                .hover-underline:hover {
                    text-decoration: underline;
                }
            `}</style>
            <Container fluid className="dashboard-content py-4">
            <div className="d-flex justify-content-center align-items-center mb-4">
                <div className="text-center">
                    <h1 className="mb-1">Dashboard ModenaCinema</h1>
                    <p className="text-muted">Panoramica generale attività e statistiche</p>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* Filtri Periodo - Collapsabile */}
            <Card className="mb-4 shadow-sm">
                <Card.Header 
                    className="bg-white d-flex justify-content-between align-items-center" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faFilter} className="me-2 text-primary" />
                        <strong>Filtri Ricerca</strong>
                    </div>
                    <FontAwesomeIcon 
                        icon={showFilters ? faChevronUp : faChevronDown} 
                        className="text-muted" 
                    />
                </Card.Header>
                <Collapse in={showFilters}>
                    <Card.Body>
                        <Form onSubmit={handleFilterSubmit}>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Periodo</Form.Label>
                                    <Form.Select
                                        value={periodoPredef}
                                        onChange={(e) => handlePeriodoChange(e.target.value)}
                                    >
                                        <option value="1_mese">Ultimo Mese</option>
                                        <option value="3_mesi">Ultimi 3 Mesi</option>
                                        <option value="6_mesi">Ultimi 6 Mesi</option>
                                        <option value="1_anno">Ultimo Anno</option>
                                        <option value="personalizzato">Personalizzato</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Data Inizio</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => {
                                            setStartDate(e.target.value);
                                            setPeriodoPredef('personalizzato');
                                        }}
                                        disabled={periodoPredef !== 'personalizzato'}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Data Fine</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => {
                                            setEndDate(e.target.value);
                                            setPeriodoPredef('personalizzato');
                                        }}
                                        disabled={periodoPredef !== 'personalizzato'}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Origine Ordine</Form.Label>
                                    <Form.Select
                                        value={origine}
                                        onChange={(e) => setOrigine(e.target.value)}
                                    >
                                        <option value="">Tutte</option>
                                        <option value="NEGOZIO">Negozio</option>
                                        <option value="SITO WEB">Sito Web</option>
                                        <option value="IBS">IBS</option>
                                        <option value="FELTRINELLI">Feltrinelli</option>
                                        <option value="RIVERSAMENTI">Riversamenti</option>
                                        <option value="ALTRO">Altro</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Stato Ordini</Form.Label>
                                    <Form.Select
                                        multiple
                                        size={3}
                                        value={statiSelezionati}
                                        onChange={(e) => {
                                            const options = e.target.options;
                                            const selected = [];
                                            for (let i = 0; i < options.length; i++) {
                                                if (options[i].selected) {
                                                    selected.push(options[i].value);
                                                }
                                            }
                                            setStatiSelezionati(selected);
                                        }}
                                        style={{ height: 'auto' }}
                                    >
                                        <option value="Da Evadere">Da Evadere</option>
                                        <option value="Confermato">Confermato</option>
                                        <option value="Concluso">Concluso</option>
                                        <option value="Spedito">Spedito</option>
                                        <option value="Parz.Concluso">Parz.Concluso</option>
                                        <option value="Annullato">Annullato</option>
                                        <option value="Rimborsato">Rimborsato</option>
                                        <option value="Non Ritirato - Contrassegno">Non Ritirato - Contrassegno</option>
                                        <option value="In attesa di pagamento">In attesa di pagamento</option>
                                        <option value="In attesa bonifico">In attesa bonifico</option>
                                        <option value="Pagamento verificato">Pagamento verificato</option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        Tieni premuto Ctrl/Cmd per selezioni multiple
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Button type="submit" variant="primary" className="me-2">
                                    Applica Filtri
                                </Button>
                                <Button variant="outline-secondary" onClick={resetFilters}>
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                        </Form>
                    </Card.Body>
                </Collapse>
            </Card>

            {stats && (
                <>
                    {/* KPI Cards */}
                    <Row>
                        {renderKPICard(
                            'Clienti Totali',
                            stats.clients.total.toLocaleString(),
                            faUsers,
                            '#007bff',
                            `${stats.clients.conversion_rate}% con ordini`
                        )}
                        {renderKPICard(
                            origine ? `Ordini ${origine.charAt(0) + origine.slice(1).toLowerCase()}` : 'Ordini Totali',
                            stats.orders.total.toLocaleString(),
                            faShoppingCart,
                            '#28a745',
                            stats.orders.by_status ? `${Object.values(stats.orders.by_status).reduce((a,b) => a+b, 0)} nel periodo` : ''
                        )}
                        {renderKPICard(
                            origine ? `Fatturato ${origine.charAt(0) + origine.slice(1).toLowerCase()}` : 'Fatturato Totale',
                            `€ ${stats.revenue.total.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`,
                            faEuroSign,
                            '#ffc107',
                            `Media: € ${stats.revenue.average_order_value.toFixed(2)}`
                        )}
                        {renderKPICard(
                            'Punti Fedeltà',
                            stats.loyalty.total_points_balance.toLocaleString(),
                            faStar,
                            '#dc3545',
                            `${stats.loyalty.clients_with_points} clienti attivi`
                        )}
                    </Row>

                    {/* Statistiche Dettagliate */}
                    <Row className="mb-4">
                        {/* Dettagli Clienti */}
                        <Col md={6} lg={3}>
                            <Card className="h-100 shadow-sm">
                                <Card.Header className="bg-primary text-white">
                                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                                    Clienti
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-2">
                                        <strong>Totali:</strong> {stats.clients.total}
                                    </div>
                                    <div className="mb-2">
                                        <strong>Con Ordini:</strong> {stats.clients.with_orders}
                                    </div>
                                    {stats.clients.ecommerce_origin > 0 && (
                                        <div className="mb-2">
                                            <strong>Origine Sito Web:</strong> {stats.clients.ecommerce_origin}
                                        </div>
                                    )}
                                    <div>
                                        <strong>Tasso Conversione:</strong> {stats.clients.conversion_rate}%
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Dettagli Ordini */}
                        <Col md={6} lg={3}>
                            <Card className="h-100 shadow-sm">
                                <Card.Header className="bg-success text-white">
                                    <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                                    Ordini {origine && `- ${origine}`}
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-2">
                                        <strong>Totali nel Periodo:</strong> {stats.orders.total}
                                    </div>
                                    {stats.orders.ecommerce > 0 && !origine && (
                                        <div className="mb-2">
                                            <strong>Da Sito Web:</strong> {stats.orders.ecommerce}
                                        </div>
                                    )}
                                    <hr />
                                    <small className="text-muted">Per Stato:</small>
                                    {Object.entries(stats.orders.by_status).map(([status, count]) => (
                                        <div key={status} className="small">
                                            <strong>{status}:</strong> {count}
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Dettagli Fatturato */}
                        <Col md={6} lg={3}>
                            <Card className="h-100 shadow-sm">
                                <Card.Header className="bg-warning text-dark">
                                    <FontAwesomeIcon icon={faEuroSign} className="me-2" />
                                    Fatturato {origine && `- ${origine}`}
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-2">
                                        <strong>Totale nel Periodo:</strong> € {stats.revenue.total.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                    </div>
                                    {stats.revenue.ecommerce > 0 && !origine && (
                                        <div className="mb-2">
                                            <strong>Da Sito Web:</strong> € {stats.revenue.ecommerce.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                        </div>
                                    )}
                                    <hr />
                                    <div className="mb-2">
                                        <strong>Ordine Medio:</strong> € {stats.revenue.average_order_value.toFixed(2)}
                                    </div>
                                    {stats.revenue.average_ecommerce_order > 0 && !origine && (
                                        <div>
                                            <strong>Ordine Medio Sito Web:</strong> € {stats.revenue.average_ecommerce_order.toFixed(2)}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Dettagli Fedeltà */}
                        <Col md={6} lg={3}>
                            <Card className="h-100 shadow-sm">
                                <Card.Header className="bg-danger text-white">
                                    <FontAwesomeIcon icon={faTrophy} className="me-2" />
                                    Fedeltà
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-2">
                                        <strong>Punti Totali:</strong> {stats.loyalty.total_points_balance.toLocaleString()}
                                    </div>
                                    <div className="mb-2">
                                        <strong>Clienti Attivi:</strong> {stats.loyalty.clients_with_points}
                                    </div>
                                    <hr />
                                    <div className="mb-2">
                                        <FontAwesomeIcon icon={faGift} className="me-1 text-success" />
                                        <strong>Assegnati:</strong> {stats.loyalty.points_awarded}
                                    </div>
                                    <div>
                                        <FontAwesomeIcon icon={faGift} className="me-1 text-danger" />
                                        <strong>Utilizzati:</strong> {stats.loyalty.points_redeemed}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Grafici Trend */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <Card className="shadow-sm">
                                <Card.Header>
                                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                    Trend Ordini Mensili
                                </Card.Header>
                                <Card.Body style={{ height: '300px' }}>
                                    {monthlyChartData && <Line data={monthlyChartData} options={chartOptions} />}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="shadow-sm">
                                <Card.Header>
                                    <FontAwesomeIcon icon={faEuroSign} className="me-2" />
                                    Trend Fatturato Mensile
                                </Card.Header>
                                <Card.Body style={{ height: '300px' }}>
                                    {revenueChartData && <Bar data={revenueChartData} options={chartOptions} />}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Top Prodotti */}
                    {stats.top_products && stats.top_products.length > 0 && (
                        <Card className="shadow-sm mb-4">
                            <Card.Header>
                                <FontAwesomeIcon icon={faTrophy} className="me-2" />
                                Top 10 Prodotti Venduti
                            </Card.Header>
                            <Card.Body>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Prodotto</th>
                                                <th className="text-center">Quantità Venduta</th>
                                                <th className="text-end">Fatturato</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stats.top_products.map((product, index) => (
                                                <tr key={product.product_key || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{product.titolo}</td>
                                                    <td className="text-center">
                                                        <span className="badge bg-primary">
                                                            {product.quantity_sold}
                                                        </span>
                                                    </td>
                                                    <td className="text-end">
                                                        € {product.revenue.toFixed(2)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Top Clienti */}
                    <Row className="mb-4">
                        {/* Top Clienti per Numero Ordini */}
                        {stats.top_clients?.by_orders && stats.top_clients.by_orders.length > 0 && (
                            <Col md={6}>
                                <Card className="shadow-sm h-100">
                                    <Card.Header className="bg-info text-white">
                                        <FontAwesomeIcon icon={faUserTie} className="me-2" />
                                        Top 10 Clienti per Numero Ordini
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Cliente</th>
                                                        <th className="text-center">Ordini</th>
                                                        <th className="text-end">Spesa Totale</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stats.top_clients.by_orders.map((client, index) => (
                                                        <tr key={client.cliente_id}>
                                                            <td>
                                                                {index < 3 && (
                                                                    <FontAwesomeIcon 
                                                                        icon={faCrown} 
                                                                        className="me-1" 
                                                                        style={{ 
                                                                            color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32' 
                                                                        }} 
                                                                    />
                                                                )}
                                                                {index + 1}
                                                            </td>
                                                            <td>
                                                                <div 
                                                                    onClick={() => handleClientClick(client.cliente_id)}
                                                                    style={{ cursor: 'pointer', color: '#0d6efd' }}
                                                                    className="hover-underline"
                                                                >
                                                                    <strong>{client.nome} {client.cognome}</strong>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="badge bg-info">
                                                                    {client.total_orders}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                € {client.total_spent.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}

                        {/* Top Clienti per Fatturato */}
                        {stats.top_clients?.by_revenue && stats.top_clients.by_revenue.length > 0 && (
                            <Col md={6}>
                                <Card className="shadow-sm h-100">
                                    <Card.Header className="bg-success text-white">
                                        <FontAwesomeIcon icon={faCrown} className="me-2" />
                                        Top 10 Clienti per Fatturato
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Cliente</th>
                                                        <th className="text-center">Ordini</th>
                                                        <th className="text-end">Spesa Totale</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stats.top_clients.by_revenue.map((client, index) => (
                                                        <tr key={client.cliente_id}>
                                                            <td>
                                                                {index < 3 && (
                                                                    <FontAwesomeIcon 
                                                                        icon={faCrown} 
                                                                        className="me-1" 
                                                                        style={{ 
                                                                            color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32' 
                                                                        }} 
                                                                    />
                                                                )}
                                                                {index + 1}
                                                            </td>
                                                            <td>
                                                                <div 
                                                                    onClick={() => handleClientClick(client.cliente_id)}
                                                                    style={{ cursor: 'pointer', color: '#0d6efd' }}
                                                                    className="hover-underline"
                                                                >
                                                                    <strong>{client.nome} {client.cognome}</strong>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="badge bg-secondary">
                                                                    {client.total_orders}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                <strong className="text-success">
                                                                    € {client.total_spent.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                                                </strong>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </>
            )}
        </Container>
        </>
    );
};

export default EcommerceDashboard;
